import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import CircularProgress from '@mui/material/CircularProgress';
import styles from './DropboxFileBrowser.module.css';

const VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'wmv', 'm4v', 'mpeg', 'mpg'];
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];

// ── Icons ───────────────────────────────────────────────────────────────────
// Inline stroke icons instead of the Material set the picker used to import:
// they match the icon language of the redesigned screens (thin, rounded,
// currentColor) and let the type tiles carry a consistent optical weight.
const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.9,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
};

const IconFolder = () => (
  <svg {...svgProps}>
    <path d="M3 8.2a2.2 2.2 0 0 1 2.2-2.2h3.3l2 2.4h6.3A2.2 2.2 0 0 1 19 10.6v6.2A2.2 2.2 0 0 1 16.8 19H5.2A2.2 2.2 0 0 1 3 16.8V8.2z" />
  </svg>
);
const IconFolderShared = () => (
  <svg {...svgProps}>
    <path d="M3 8.2a2.2 2.2 0 0 1 2.2-2.2h3.3l2 2.4h6.3A2.2 2.2 0 0 1 19 10.6v6.2A2.2 2.2 0 0 1 16.8 19H5.2A2.2 2.2 0 0 1 3 16.8V8.2z" />
    <circle cx="11" cy="13" r="1.7" />
    <path d="M8 17c0-1.3 1.3-2.2 3-2.2s3 .9 3 2.2" />
  </svg>
);
const IconVideo = () => (
  <svg {...svgProps}>
    <rect x="2.8" y="6" width="12.6" height="12" rx="2.4" />
    <path d="M21.2 8.2v7.6l-5.8-3.8 5.8-3.8z" />
  </svg>
);
const IconImage = () => (
  <svg {...svgProps}>
    <rect x="3" y="4.6" width="18" height="14.8" rx="2.6" />
    <circle cx="8.6" cy="10" r="1.5" />
    <path d="M4 17.2l4.6-4.2 3.4 3 2.8-2.4L20 17.6" />
  </svg>
);
const IconFile = () => (
  <svg {...svgProps}>
    <path d="M13.6 3.2H7.4A2.4 2.4 0 0 0 5 5.6v12.8a2.4 2.4 0 0 0 2.4 2.4h9.2a2.4 2.4 0 0 0 2.4-2.4V8.6l-5.4-5.4z" />
    <path d="M13.4 3.4v5.2h5.2" />
  </svg>
);
const IconSearch = ({ className }) => (
  <svg {...svgProps} className={className}>
    <circle cx="11" cy="11" r="6.6" />
    <line x1="16" y1="16" x2="20.6" y2="20.6" />
  </svg>
);
const IconClose = () => (
  <svg {...svgProps} strokeWidth={2}>
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </svg>
);
const IconHome = () => (
  <svg {...svgProps}>
    <path d="M4 10.6 12 4l8 6.6" />
    <path d="M6 9.8V19h12V9.8" />
  </svg>
);
const IconArrowLeft = () => (
  <svg {...svgProps} strokeWidth={2}>
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="11 18 5 12 11 6" />
  </svg>
);
const IconChevronRight = () => (
  <svg {...svgProps} strokeWidth={2}>
    <polyline points="9 5 16 12 9 19" />
  </svg>
);
const IconCheck = () => (
  <svg {...svgProps} strokeWidth={3.2}>
    <polyline points="20 6.5 9.5 17 4 11.5" />
  </svg>
);
const IconDownload = () => (
  <svg {...svgProps} strokeWidth={2}>
    <path d="M12 3.5v11" />
    <polyline points="7.5 10 12 14.5 16.5 10" />
    <path d="M4.5 19.5h15" />
  </svg>
);
// Dropbox's own mark for the header tile — the picker should say which cloud
// it is talking to without a text label doing all the work.
const IconDropbox = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M6.5 3 1 6.6l5.5 3.5L12 6.6 6.5 3zm11 0L12 6.6l5.5 3.5L23 6.6 17.5 3zM1 13.6l5.5 3.5L12 13.6 6.5 10.1 1 13.6zm16.5-3.5L12 13.6l5.5 3.5 5.5-3.5-5.5-3.5zM6.6 18.3l5.4 3.5 5.4-3.5-5.4-3.4-5.4 3.4z" />
  </svg>
);

// Type of a listing row — drives both the glyph and the tile tint.
const getItemKind = (fileName, isFolder) => {
  if (isFolder) return 'folder';
  const extension = (fileName || '').split('.').pop().toLowerCase();
  if (VIDEO_EXTENSIONS.includes(extension)) return 'video';
  if (IMAGE_EXTENSIONS.includes(extension)) return 'image';
  return 'file';
};

const KIND_LABEL = {
  folder: 'Folder', video: 'Video', image: 'Photo', file: 'File',
};

const getFileIcon = (fileName, isFolder, isShared = false) => {
  if (isFolder) return isShared ? <IconFolderShared /> : <IconFolder />;
  const kind = getItemKind(fileName, false);
  if (kind === 'video') return <IconVideo />;
  if (kind === 'image') return <IconImage />;
  return <IconFile />;
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '';

  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Math.round((bytes / (1024 ** i)) * 100) / 100} ${sizes[i]}`;
};

export const DropboxFileBrowser = ({ accessToken, onClose, onFilesSelected, onShowMessage, onTokenExpired }) => {
  const [currentPath, setCurrentPath] = useState('');
  const [pathHistory, setPathHistory] = useState(['']);
  const [items, setItems] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingFolderId, setLoadingFolderId] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sharedFolders, setSharedFolders] = useState([]);
  const [currentNamespaceId, setCurrentNamespaceId] = useState(null);
  const [viewMode, setViewMode] = useState('my_files'); // 'my_files' or 'shared_files'

  // Pagination state
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const contentRef = React.useRef(null);

  // Search state
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchCursor, setSearchCursor] = useState(null);
  const [hasMoreSearchResults, setHasMoreSearchResults] = useState(false);
  const searchTimeoutRef = React.useRef(null);

  // Selection state for shift/ctrl clicking
  const [lastSelectedFileIndex, setLastSelectedFileIndex] = useState(null);

  // Background loading state for shared folders
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
  const [hasMoreSharedFolders, setHasMoreSharedFolders] = useState(false);
  const backgroundLoadingRef = React.useRef(false);

  const PAGE_SIZE = 25;

  // Use search results when searching, otherwise use items
  // For shared_files view at root (no namespace), use local filtering since API can't search other accounts
  const filteredItems = useMemo(() => {
    if (searchQuery.trim()) {
      // Local filter for shared_files at root level (API doesn't search other accounts' content)
      if (viewMode === 'shared_files' && !currentNamespaceId) {
        const query = searchQuery.toLowerCase().trim();
        return items.filter((item) => item.name.toLowerCase().includes(query));
      }
      return searchResults;
    }
    return items;
  }, [searchQuery, searchResults, items, viewMode, currentNamespaceId]);

  // Search function using Dropbox search_v2 API
  const performSearch = async (query, cursorParam = null) => {
    if (!query) return;

    if (!cursorParam) {
      setIsSearching(true);
      setSearchResults([]);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const headers = {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      };

      // Add namespace header if in shared folder context
      if (currentNamespaceId) {
        headers['Dropbox-API-Path-Root'] = JSON.stringify({
          '.tag': 'namespace_id',
          namespace_id: currentNamespaceId,
        });
      }

      let response;

      if (cursorParam) {
        // Continue search
        response = await axios.post(
          'https://api.dropboxapi.com/2/files/search/continue_v2',
          { cursor: cursorParam },
          { headers },
        );
      } else {
        // New search
        // For shared_files view, search without path restriction to find across all shared folders
        const searchOptions = {
          max_results: PAGE_SIZE,
          // Don't use file_categories - it doesn't support folders
          // We'll filter results manually to show folders + videos
        };

        // Only add path restriction for my_files view with a specific path
        if (viewMode === 'my_files' && currentPath) {
          searchOptions.path = currentPath;
        }

        response = await axios.post(
          'https://api.dropboxapi.com/2/files/search_v2',
          {
            query,
            options: searchOptions,
          },
          { headers },
        );
      }

      // eslint-disable-next-line no-console
      console.log('[DROPBOX_SHARE] Search response:', response.data);

      // Extract matches and convert to item format
      const matches = response.data.matches || [];
      const searchItems = matches
        .map((match) => match.metadata?.metadata)
        .filter((item) => {
          if (!item) return false;
          // Filter folders, video files, and image files
          if (item['.tag'] === 'folder') return true;
          if (item['.tag'] === 'file') {
            const ext = item.name.split('.').pop().toLowerCase();
            return VIDEO_EXTENSIONS.includes(ext) || IMAGE_EXTENSIONS.includes(ext);
          }
          return false;
        });

      if (cursorParam) {
        setSearchResults((prev) => [...prev, ...searchItems]);
      } else {
        setSearchResults(searchItems);
      }

      setSearchCursor(response.data.cursor);
      setHasMoreSearchResults(response.data.has_more);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Search error:', err);
    } finally {
      setIsSearching(false);
      setIsLoadingMore(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!searchQuery.trim()) {
      setSearchResults([]);
      setSearchCursor(null);
      setHasMoreSearchResults(false);
      return;
    }

    // Skip API search for shared_files at root level - uses local filtering instead
    // (Dropbox search API can't search other accounts' content)
    if (viewMode === 'shared_files' && !currentNamespaceId) {
      return;
    }

    // Debounce search by 400ms
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(searchQuery.trim());
    }, 400);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line
  }, [searchQuery, viewMode, currentNamespaceId]);

  // Helper to map shared folder data to our format
  const mapSharedFolder = (folder) => ({
    '.tag': 'folder',
    id: folder.shared_folder_id,
    name: folder.name,
    path_display: folder.path_lower || `/${folder.name}`,
    shared_folder_id: folder.shared_folder_id,
    is_shared: true,
    preview_url: folder.preview_url,
    owner_display_name: folder.owner_display_names?.[0] || folder.owner_team?.name || 'Unknown',
  });

  // Function to fetch shared folders with pagination (25 items per page)
  const fetchSharedFolders = async (cursorParam = null) => {
    try {
      // eslint-disable-next-line no-console
      console.log('[DROPBOX_SHARE] Fetching shared folders, cursor:', cursorParam);

      let response;

      if (cursorParam) {
        // Continue from cursor
        response = await axios.post(
          'https://api.dropboxapi.com/2/sharing/list_folders/continue',
          { cursor: cursorParam },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          },
        );
      } else {
        // First request
        response = await axios.post(
          'https://api.dropboxapi.com/2/sharing/list_folders',
          { limit: PAGE_SIZE },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          },
        );
      }

      const folders = response.data.entries || [];
      const newCursor = response.data.cursor;

      // eslint-disable-next-line no-console
      console.log('[DROPBOX_SHARE] Fetched:', folders.length, 'hasMore:', !!newCursor);

      return {
        folders: folders.map(mapSharedFolder),
        cursor: newCursor,
        hasMore: !!newCursor,
      };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Error fetching shared folders:', err);
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Error response:', err.response?.data);
      return { folders: [], cursor: null, hasMore: false };
    }
  };

  // Function to fetch received files (files shared directly with user)
  const fetchReceivedFiles = async (cursorParam = null) => {
    try {
      // eslint-disable-next-line no-console
      console.log('[DROPBOX_SHARE] Fetching received files, cursor:', cursorParam);

      let response;

      if (cursorParam) {
        response = await axios.post(
          'https://api.dropboxapi.com/2/sharing/list_received_files/continue',
          { cursor: cursorParam },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          },
        );
      } else {
        response = await axios.post(
          'https://api.dropboxapi.com/2/sharing/list_received_files',
          { limit: PAGE_SIZE },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          },
        );
      }

      const files = response.data.entries || [];
      const newCursor = response.data.cursor;

      // eslint-disable-next-line no-console
      console.log('[DROPBOX_SHARE] Fetched received files:', files.length, 'hasMore:', !!newCursor);

      // Map received files to our format and filter for video + image files
      const mappedFiles = files
        .filter((file) => {
          const ext = file.name.split('.').pop().toLowerCase();
          return VIDEO_EXTENSIONS.includes(ext) || IMAGE_EXTENSIONS.includes(ext);
        })
        .map((file) => {
          const ext = file.name.split('.').pop().toLowerCase();
          const isImage = IMAGE_EXTENSIONS.includes(ext);
          return {
            '.tag': 'file',
            id: file.id,
            name: file.name,
            path_display: file.path_display || `/${file.name}`,
            size: file.size,
            is_shared: true,
            owner_display_name: file.owner_display_names?.[0] || file.owner_team?.name || 'Unknown',
            preview_url: file.preview_url,
            fileType: isImage ? 'image' : 'video',
          };
        });

      return {
        files: mappedFiles,
        cursor: newCursor,
        hasMore: !!newCursor,
      };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Error fetching received files:', err);
      // Check if it's a scope permission error - gracefully handle
      if (err.response?.status === 401 || err.response?.data?.error?.['.tag'] === 'no_permission') {
        // eslint-disable-next-line no-console
        console.log('[DROPBOX_SHARE] No permission for received files, skipping');
      }
      return { files: [], cursor: null, hasMore: false };
    }
  };

  // Function to recursively get all media files (videos + images) from a folder
  const getAllMediaFromFolder = async (folderPath, namespaceId = null) => {
    try {
      const headers = {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      };

      // Add namespace header if we're in a shared folder context
      if (namespaceId) {
        headers['Dropbox-API-Path-Root'] = JSON.stringify({
          '.tag': 'namespace_id',
          namespace_id: namespaceId,
        });
      }

      // Collect all items across paginated responses
      let allItems = [];
      let hasMore = true;
      let cursor = null;

      while (hasMore) {
        let response;

        if (cursor) {
          // Continue from previous cursor
          // eslint-disable-next-line no-await-in-loop
          response = await axios.post(
            'https://api.dropboxapi.com/2/files/list_folder/continue',
            { cursor },
            { headers },
          );
        } else {
          // Initial request
          // eslint-disable-next-line no-await-in-loop
          response = await axios.post(
            'https://api.dropboxapi.com/2/files/list_folder',
            {
              path: folderPath,
              recursive: true, // This will get all files from subfolders too
              include_deleted: false,
              include_mounted_folders: true,
            },
            { headers },
          );
        }

        const entries = response.data.entries || [];
        allItems = [...allItems, ...entries];
        hasMore = response.data.has_more;
        cursor = response.data.cursor;

        // eslint-disable-next-line no-console
        console.log('[DROPBOX] getAllMediaFromFolder - fetched:', entries.length, 'total:', allItems.length, 'hasMore:', hasMore);
      }

      // Filter to get video and image files
      const mediaFiles = allItems.filter((item) => {
        if (item['.tag'] === 'file') {
          const fileName = item.name.toLowerCase();
          const extension = fileName.split('.').pop();
          return VIDEO_EXTENSIONS.includes(extension) || IMAGE_EXTENSIONS.includes(extension);
        }
        return false;
      });

      return mediaFiles.map((file) => {
        const extension = file.name.split('.').pop().toLowerCase();
        const isImage = IMAGE_EXTENSIONS.includes(extension);
        return {
          id: file.id,
          name: file.name,
          size: file.size,
          path: file.path_display,
          fileType: isImage ? 'image' : 'video',
          namespaceId, // Include namespace ID for shared folder access
        };
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading media from folder:', err);
      return [];
    }
  };

  // Helper to filter items (folders + videos + images)
  const filterItems = (allItems) => allItems.filter((item) => {
    if (item['.tag'] === 'folder') return true;
    if (item['.tag'] === 'file') {
      const fileName = item.name.toLowerCase();
      const extension = fileName.split('.').pop();
      return VIDEO_EXTENSIONS.includes(extension) || IMAGE_EXTENSIONS.includes(extension);
    }
    return false;
  });

  const loadFolder = async (path, namespaceId = null) => {
    setIsLoading(true);
    setError(null);
    setCursor(null);
    setHasMore(false);

    // eslint-disable-next-line no-console
    console.log('[DROPBOX_SHARE] loadFolder called with path:', path, 'namespaceId:', namespaceId);

    try {
      const headers = {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      };

      // Add namespace header if we're in a shared folder context
      if (namespaceId) {
        headers['Dropbox-API-Path-Root'] = JSON.stringify({
          '.tag': 'namespace_id',
          namespace_id: namespaceId,
        });
        // eslint-disable-next-line no-console
        console.log('[DROPBOX_SHARE] Using namespace header:', headers['Dropbox-API-Path-Root']);
      }

      const response = await axios.post(
        'https://api.dropboxapi.com/2/files/list_folder',
        {
          path: path || '',
          recursive: false,
          include_deleted: false,
          include_mounted_folders: true,
          limit: PAGE_SIZE,
        },
        { headers },
      );

      // eslint-disable-next-line no-console
      console.log('[DROPBOX_SHARE] list_folder response:', response.data);

      const filteredItems = filterItems(response.data.entries || []);

      // eslint-disable-next-line no-console
      console.log('[DROPBOX_SHARE] Filtered items:', filteredItems.length, 'hasMore:', response.data.has_more);

      setItems(filteredItems);
      setCursor(response.data.cursor);
      setHasMore(response.data.has_more);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Error loading Dropbox folder:', err);
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Error response:', err.response?.data);

      // Check if token expired (401 Unauthorized)
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        if (onTokenExpired) {
          onTokenExpired();
          return;
        }
      }

      setError('Failed to load folder contents');
    } finally {
      setIsLoading(false);
    }
  };

  // Load more items (for infinite scroll)
  const loadMoreItems = async () => {
    if (isLoadingMore || !hasMore || !cursor) return;

    setIsLoadingMore(true);

    try {
      if (viewMode === 'shared_files') {
        // Load more shared folders
        const result = await fetchSharedFolders(cursor);
        setItems((prev) => [...prev, ...result.folders]);
        setSharedFolders((prev) => [...prev, ...result.folders]);
        setCursor(result.cursor);
        setHasMore(result.hasMore);
      } else {
        // Load more files
        const headers = {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        };

        if (currentNamespaceId) {
          headers['Dropbox-API-Path-Root'] = JSON.stringify({
            '.tag': 'namespace_id',
            namespace_id: currentNamespaceId,
          });
        }

        const response = await axios.post(
          'https://api.dropboxapi.com/2/files/list_folder/continue',
          { cursor },
          { headers },
        );

        const filteredItems = filterItems(response.data.entries || []);

        // eslint-disable-next-line no-console
        console.log('[DROPBOX_SHARE] Loaded more:', filteredItems.length, 'hasMore:', response.data.has_more);

        setItems((prev) => [...prev, ...filteredItems]);
        setCursor(response.data.cursor);
        setHasMore(response.data.has_more);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Error loading more items:', err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Background load remaining shared folders
  const loadRemainingSharedFolders = async (cursorToUse) => {
    if (!cursorToUse || backgroundLoadingRef.current) return;

    backgroundLoadingRef.current = true;
    setIsBackgroundLoading(true);

    let currentCursor = cursorToUse;

    try {
      while (currentCursor) {
        // Check if we should stop (e.g., view changed)
        if (!backgroundLoadingRef.current) break;

        // eslint-disable-next-line no-await-in-loop
        const result = await fetchSharedFolders(currentCursor);

        if (!backgroundLoadingRef.current) break;

        // Append new folders to both items and sharedFolders
        setItems((prev) => [...prev, ...result.folders]);
        setSharedFolders((prev) => [...prev, ...result.folders]);

        currentCursor = result.cursor;
        setHasMoreSharedFolders(!!currentCursor);

        // eslint-disable-next-line no-console
        console.log('[DROPBOX_SHARE] Background loaded:', result.folders.length, 'more to load:', !!currentCursor);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Background loading error:', err);
    } finally {
      backgroundLoadingRef.current = false;
      setIsBackgroundLoading(false);
      setHasMoreSharedFolders(false);
    }
  };

  // Background load remaining received files
  const loadRemainingReceivedFiles = async (cursorToUse) => {
    if (!cursorToUse) return;

    let currentCursor = cursorToUse;

    try {
      while (currentCursor && backgroundLoadingRef.current !== false) {
        // eslint-disable-next-line no-await-in-loop
        const result = await fetchReceivedFiles(currentCursor);

        if (backgroundLoadingRef.current === false) break;

        // Append new files to items
        setItems((prev) => [...prev, ...result.files]);

        currentCursor = result.cursor;

        // eslint-disable-next-line no-console
        console.log('[DROPBOX_SHARE] Background loaded files:', result.files.length, 'more to load:', !!currentCursor);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Background loading files error:', err);
    }
  };

  // Load shared folders view (folders + received files)
  const loadSharedFoldersView = async () => {
    // Stop any ongoing background loading
    backgroundLoadingRef.current = false;

    setIsLoading(true);
    setError(null);
    setCursor(null);
    setHasMore(false);
    setHasMoreSharedFolders(false);

    try {
      // Fetch both shared folders and received files in parallel
      const [foldersResult, filesResult] = await Promise.all([
        fetchSharedFolders(),
        fetchReceivedFiles(),
      ]);

      // Combine folders and files (folders first, then files)
      const combinedItems = [...foldersResult.folders, ...filesResult.files];

      // eslint-disable-next-line no-console
      console.log('[DROPBOX_SHARE] Loaded:', foldersResult.folders.length, 'folders,', filesResult.files.length, 'files');

      setSharedFolders(foldersResult.folders);
      setItems(combinedItems);
      setHasMoreSharedFolders(foldersResult.hasMore || filesResult.hasMore);

      // Start background loading if there are more folders
      if (foldersResult.hasMore && foldersResult.cursor) {
        // eslint-disable-next-line no-console
        console.log('[DROPBOX_SHARE] Starting background loading of remaining shared folders...');
        loadRemainingSharedFolders(foldersResult.cursor);
      }

      // Background load remaining received files if any
      if (filesResult.hasMore && filesResult.cursor) {
        loadRemainingReceivedFiles(filesResult.cursor);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Error loading shared folders view:', err);
      setError('Failed to load shared folders');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle view mode change
  const handleViewModeChange = (mode) => {
    // Stop background loading when switching views
    backgroundLoadingRef.current = false;

    setViewMode(mode);
    setCurrentPath('');
    setPathHistory(['']);
    setCurrentNamespaceId(null);
    setSearchQuery('');
    setCursor(null);
    setHasMore(false);
    setHasMoreSharedFolders(false);
    setIsBackgroundLoading(false);
  };

  // Scroll handler for infinite scroll
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Load more when scrolled to bottom (with 100px threshold)
    if (scrollHeight - scrollTop - clientHeight < 100 && !isLoadingMore) {
      // If searching, load more search results
      if (searchQuery.trim() && hasMoreSearchResults && searchCursor) {
        performSearch(searchQuery.trim(), searchCursor);
      } else if (!searchQuery.trim() && hasMore) {
        // Otherwise load more folder items
        loadMoreItems();
      }
    }
  };

  useEffect(() => {
    if (accessToken) {
      // If in shared_files view at root (no namespace), show shared folders list
      // Otherwise, load the folder contents (works for both my_files and inside a shared folder)
      if (viewMode === 'shared_files' && !currentNamespaceId) {
        loadSharedFoldersView();
      } else {
        loadFolder(currentPath, currentNamespaceId);
      }
    }
    // eslint-disable-next-line
  }, [currentPath, accessToken, currentNamespaceId, viewMode]);

  const handleFolderClick = (folder) => {
    // Stop background loading when navigating into any folder
    backgroundLoadingRef.current = false;
    setIsBackgroundLoading(false);

    // If folder is an object (from the items list), extract the path
    const folderPath = typeof folder === 'string' ? folder : folder.path_display;

    // eslint-disable-next-line no-console
    console.log('[DROPBOX_SHARE] handleFolderClick:', folder);

    // Check if this is a shared folder (has shared_folder_id)
    if (typeof folder === 'object' && folder.shared_folder_id) {
      // eslint-disable-next-line no-console
      console.log('[DROPBOX_SHARE] Entering shared folder with namespace:', folder.shared_folder_id);
      // Entering a shared folder - set the namespace but stay in current view mode
      setCurrentNamespaceId(folder.shared_folder_id);
      setPathHistory(['']); // Reset path history for shared folder context
      setCurrentPath(''); // Start from root of the shared folder
    } else {
      setPathHistory([...pathHistory, folderPath]);
      setCurrentPath(folderPath);
    }
    setSearchQuery(''); // Clear search when navigating
    setLastSelectedFileIndex(null); // Reset selection index when navigating
  };

  const handleBackClick = () => {
    if (pathHistory.length > 1) {
      const newHistory = [...pathHistory];
      newHistory.pop();
      const previousPath = newHistory[newHistory.length - 1];
      setPathHistory(newHistory);
      setCurrentPath(previousPath);
      setSearchQuery(''); // Clear search when navigating back
      setLastSelectedFileIndex(null); // Reset selection index when navigating back
    } else if (currentNamespaceId) {
      // If we're in a shared folder namespace and at its root, go back to shared folders list
      setCurrentNamespaceId(null);
      setPathHistory(['']);
      setCurrentPath('');
      setSearchQuery('');
      setLastSelectedFileIndex(null); // Reset selection index
      // If in shared_files view, reload the shared folders list
      if (viewMode === 'shared_files') {
        loadSharedFoldersView();
      }
    }
  };

  const handleHomeClick = () => {
    setPathHistory(['']);
    setCurrentPath('');
    setCurrentNamespaceId(null); // Clear namespace when going home
    setSearchQuery(''); // Clear search when going home
    setLastSelectedFileIndex(null); // Reset selection index when going home
  };

  const handleFileToggle = (file, event) => {
    const fileId = file.id;
    const isSelected = selectedFiles.some((f) => f.id === fileId);

    // Get list of all files in current view (for shift-click range selection)
    const allFiles = filteredItems.filter((item) => item['.tag'] === 'file');
    const currentFileIndex = allFiles.findIndex((item) => item.id === fileId);

    // Convert file to the format we store
    const extension = file.name.split('.').pop().toLowerCase();
    const isImage = IMAGE_EXTENSIONS.includes(extension);
    const fileToAdd = {
      id: fileId,
      name: file.name,
      size: file.size,
      path: file.path_display,
      fileType: isImage ? 'image' : 'video',
      namespaceId: currentNamespaceId, // Include namespace ID for shared folder access
    };

    // Handle Shift+Click - Range selection
    if (event?.shiftKey && currentFileIndex !== -1) {
      // If no file was previously selected, start from index 0 (first file)
      const startIndex = lastSelectedFileIndex !== null
        ? Math.min(lastSelectedFileIndex, currentFileIndex)
        : 0;
      const endIndex = lastSelectedFileIndex !== null
        ? Math.max(lastSelectedFileIndex, currentFileIndex)
        : currentFileIndex;

      // Get all files in the range
      const filesInRange = allFiles.slice(startIndex, endIndex + 1).map((item) => {
        const ext = item.name.split('.').pop().toLowerCase();
        const isImg = IMAGE_EXTENSIONS.includes(ext);
        return {
          id: item.id,
          name: item.name,
          size: item.size,
          path: item.path_display,
          fileType: isImg ? 'image' : 'video',
          namespaceId: currentNamespaceId, // Include namespace ID for shared folder access
        };
      });

      // Add all files in range to selection (avoid duplicates)
      const existingFileIds = selectedFiles.map((f) => f.id);
      const newFiles = filesInRange.filter((f) => !existingFileIds.includes(f.id));
      setSelectedFiles([...selectedFiles, ...newFiles]);
      setLastSelectedFileIndex(currentFileIndex);
      return;
    }

    // Handle Ctrl+Click (Windows) or Cmd+Click (Mac) - Toggle selection
    if (event?.ctrlKey || event?.metaKey) {
      if (isSelected) {
        setSelectedFiles(selectedFiles.filter((f) => f.id !== fileId));
      } else {
        setSelectedFiles([...selectedFiles, fileToAdd]);
      }
      setLastSelectedFileIndex(currentFileIndex);
      return;
    }

    // Normal click - Toggle single file
    if (isSelected) {
      setSelectedFiles(selectedFiles.filter((f) => f.id !== fileId));
    } else {
      setSelectedFiles([...selectedFiles, fileToAdd]);
    }
    setLastSelectedFileIndex(currentFileIndex);
  };

  const handleFolderToggle = async (folder) => {
    const folderId = folder.id;
    const isSelected = selectedFolders.some((f) => f.id === folderId);

    if (isSelected) {
      // Deselect folder and remove all its files
      setSelectedFolders(selectedFolders.filter((f) => f.id !== folderId));
      const folderData = selectedFolders.find((f) => f.id === folderId);
      if (folderData && folderData.files) {
        // Remove all files that belong to this folder
        const folderFileIds = folderData.files.map((file) => file.id);
        setSelectedFiles(selectedFiles.filter((f) => !folderFileIds.includes(f.id)));
      }
    } else {
      // Select folder and get all its media files (videos + images)
      setLoadingFolderId(folderId);
      // Use shared_folder_id as namespace if this is a shared folder, otherwise use current namespace
      const namespaceToUse = folder.shared_folder_id || currentNamespaceId;
      // For shared folders at root level, use empty string as path; otherwise use folder path
      const pathToUse = folder.shared_folder_id ? '' : folder.path_display;
      const mediaFiles = await getAllMediaFromFolder(pathToUse, namespaceToUse);
      setLoadingFolderId(null);

      if (mediaFiles.length === 0) {
        // Show toast if no media found
        if (onShowMessage) {
          onShowMessage('Nothing to select');
        }
      } else {
        // Mark each file with the folder name for category tracking
        const filesWithFolderName = mediaFiles.map((file) => ({
          ...file,
          folder_name: folder.name,
        }));

        // Add folder to selected folders
        setSelectedFolders([...selectedFolders, {
          id: folderId,
          name: folder.name,
          path: folder.path_display,
          files: filesWithFolderName,
        }]);

        // Add all video files to selected files (avoid duplicates)
        const existingFileIds = selectedFiles.map((f) => f.id);
        const newFiles = filesWithFolderName.filter((file) => !existingFileIds.includes(file.id));
        setSelectedFiles([...selectedFiles, ...newFiles]);
      }
    }
  };

  const handleImport = () => {
    if (selectedFiles.length > 0) {
      onFilesSelected(selectedFiles);
    }
  };

  const isFileSelected = (fileId) => selectedFiles.some((f) => f.id === fileId);
  const isFolderSelected = (folderId) => selectedFolders.some((f) => f.id === folderId);

  const handleClearSelection = () => {
    setSelectedFiles([]);
    setSelectedFolders([]);
    setLastSelectedFileIndex(null);
  };

  const handleSelectAll = () => {
    // Select all files in current folder
    const allFiles = items.filter((item) => item['.tag'] === 'file').map((file) => {
      const extension = file.name.split('.').pop().toLowerCase();
      const isImage = IMAGE_EXTENSIONS.includes(extension);
      return {
        id: file.id,
        name: file.name,
        size: file.size,
        path: file.path_display,
        fileType: isImage ? 'image' : 'video',
        namespaceId: currentNamespaceId, // Include namespace ID for shared folder access
      };
    });
    setSelectedFiles(allFiles);
  };

  const getCurrentFolderName = () => {
    // If we're in a shared folder namespace
    if (currentNamespaceId && !currentPath) {
      const sharedFolder = sharedFolders.find((f) => f.shared_folder_id === currentNamespaceId);
      return sharedFolder ? sharedFolder.name : 'Shared Folder';
    }
    if (!currentPath || currentPath === '') {
      return viewMode === 'shared_files' ? 'Shared' : 'Dropbox';
    }
    return currentPath.split('/').pop();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  // Selected-file totals for the footer summary.
  const selectedBytes = selectedFiles.reduce((sum, f) => sum + (f.size || 0), 0);
  const fileCountInFolder = items.filter((item) => item['.tag'] === 'file').length;
  const canGoBack = pathHistory.length > 1 || !!currentNamespaceId;

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
      >
        {/* Brand row — says which cloud this is and owns the close action. */}
        <div className={styles.head}>
          <div className={styles.brand}>
            <span className={styles.brandIcon}><IconDropbox /></span>
            <div className={styles.brandText}>
              <p className={styles.brandTitle}>Dropbox</p>
              <p className={styles.brandSub}>Pick the videos and photos you want to import</p>
            </div>
          </div>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            <IconClose />
          </button>
        </div>

        {/* Toolbar — source segments (one tap, no menu) + search. */}
        <div className={styles.toolbar}>
          <div className={styles.segment} role="tablist" aria-label="Dropbox source">
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'my_files'}
              className={`${styles.segmentBtn} ${viewMode === 'my_files' ? styles.segmentBtnActive : ''}`}
              onClick={() => handleViewModeChange('my_files')}
            >
              <IconFolder />
              My Files
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'shared_files'}
              className={`${styles.segmentBtn} ${viewMode === 'shared_files' ? styles.segmentBtnActive : ''}`}
              onClick={() => handleViewModeChange('shared_files')}
            >
              <IconFolderShared />
              Shared with me
            </button>
          </div>
          <div className={styles.searchContainer}>
            <IconSearch className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search folders and files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className={styles.searchClearButton}
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                <IconClose />
              </button>
            )}
          </div>
        </div>

        {/* Path bar — back + breadcrumb on the left, bulk selection on the
            right, so the footer can hold nothing but Cancel and Import. */}
        <div className={styles.navigation}>
          {canGoBack && (
            <button className={styles.backButton} onClick={handleBackClick}>
              <IconArrowLeft />
              Back
            </button>
          )}
          <div className={styles.breadcrumb}>
            <button
              className={styles.breadcrumbButton}
              onClick={handleHomeClick}
              disabled={currentPath === '' && !currentNamespaceId}
              aria-label="Go to top level"
            >
              <IconHome />
            </button>
            {(currentPath || currentNamespaceId) && (
              <>
                <span className={styles.breadcrumbSeparator}>/</span>
                <span className={styles.breadcrumbCurrent}>{getCurrentFolderName()}</span>
              </>
            )}
          </div>
          {currentNamespaceId && (
            <span className={styles.sharedIndicator}>Shared folder</span>
          )}
          <span className={styles.spacer} />
          {fileCountInFolder > 0 && selectedFiles.length < fileCountInFolder && (
            <button type="button" className={styles.ghostButton} onClick={handleSelectAll}>
              Select all
            </button>
          )}
          {selectedFiles.length > 0 && (
            <button type="button" className={styles.ghostButton} onClick={handleClearSelection}>
              Clear
            </button>
          )}
        </div>

        {/* Content */}
        <div className={styles.content} onScroll={handleScroll} ref={contentRef}>
          {(isLoading || (isSearching && !(viewMode === 'shared_files' && !currentNamespaceId))) && (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <p>{isSearching ? 'Searching...' : 'Loading...'}</p>
            </div>
          )}

          {error && !isSearching && (
            <div className={styles.errorState}>
              <span className={`${styles.stateHalo} ${styles.stateHaloError}`}>
                <IconClose />
              </span>
              <p>{error}</p>
              <button
                className={styles.retryButton}
                onClick={() => loadFolder(currentPath)}
              >
                Try again
              </button>
            </div>
          )}

          {!isLoading && !isBackgroundLoading && (!isSearching || (viewMode === 'shared_files' && !currentNamespaceId)) && !error && filteredItems.length === 0 && !searchQuery.trim() && (
            <div className={styles.emptyState}>
              <span className={styles.stateHalo}>
                <IconFolder />
              </span>
              <p>{viewMode === 'shared_files' ? 'Nothing has been shared with you yet' : 'This folder is empty'}</p>
            </div>
          )}

          {!isLoading && (!isSearching || (viewMode === 'shared_files' && !currentNamespaceId)) && !error && filteredItems.length === 0 && searchQuery.trim() && !isBackgroundLoading && (
            <div className={styles.emptyState}>
              <span className={styles.stateHalo}>
                <IconSearch />
              </span>
              <p>
                No results found for &ldquo;
                {searchQuery}
                &rdquo;
              </p>
              {viewMode === 'shared_files' && !currentNamespaceId && (
                <p className={styles.searchHint}>All shared folders have been loaded</p>
              )}
            </div>
          )}

          {!isLoading && filteredItems.length === 0 && searchQuery.trim() && isBackgroundLoading && viewMode === 'shared_files' && !currentNamespaceId && (
            <div className={styles.emptyState}>
              <CircularProgress size={32} style={{ color: '#5aa5ef', marginBottom: 16 }} />
              <p>Searching through shared folders...</p>
              <p className={styles.searchHint}>More folders are still loading</p>
            </div>
          )}

          {!isLoading && (!isSearching || (viewMode === 'shared_files' && !currentNamespaceId)) && !error && filteredItems.length > 0 && (
            <div className={styles.itemList}>
              {filteredItems.map((item) => {
                const isFolder = item['.tag'] === 'folder';
                const isFile = item['.tag'] === 'file';
                const isSelected = isFile ? isFileSelected(item.id) : isFolderSelected(item.id);
                const kind = getItemKind(item.name, isFolder);
                const kindClass = {
                  folder: styles.itemIconFolder,
                  video: styles.itemIconVideo,
                  image: styles.itemIconImage,
                  file: '',
                }[kind];

                return (
                  <div
                    key={item.id}
                    className={`${styles.item} ${isFolder ? styles.itemFolder : ''} ${isSelected ? styles.itemSelected : ''} ${item.is_shared ? styles.itemShared : ''}`}
                    onClick={() => {
                      if (isFolder) {
                        handleFolderClick(item);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && isFolder) {
                        handleFolderClick(item);
                      }
                    }}
                  >
                    <div className={`${styles.itemIcon} ${kindClass}`}>
                      {getFileIcon(item.name, isFolder, item.is_shared)}
                    </div>

                    <div className={styles.itemInfo}>
                      <div className={styles.itemName}>{item.name}</div>
                      {/* Type + size on one meta line — the type label makes a
                          long mixed listing scannable without reading names. */}
                      <div className={styles.itemSize}>
                        <span className={styles.itemKind}>{KIND_LABEL[kind]}</span>
                        {isFile && !!item.size && (
                          <>
                            <span className={styles.itemDot} aria-hidden="true" />
                            {formatFileSize(item.size)}
                          </>
                        )}
                        {isFolder && (
                          <>
                            <span className={styles.itemDot} aria-hidden="true" />
                            Tap to open
                          </>
                        )}
                      </div>
                      {item.is_shared && item.owner_display_name && (
                        <div className={styles.itemOwner}>
                          Shared by
                          {' '}
                          {item.owner_display_name}
                        </div>
                      )}
                    </div>

                    {isFolder && (
                      <span className={styles.itemChevron} aria-hidden="true">
                        <IconChevronRight />
                      </span>
                    )}

                    {isFile && (
                      <button
                        className={styles.selectButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFileToggle(item, e);
                        }}
                        aria-pressed={isSelected}
                        aria-label={isSelected ? `Deselect ${item.name}` : `Select ${item.name}`}
                      >
                        <span className={`${styles.checkbox} ${isSelected ? styles.checkboxOn : ''}`}>
                          <IconCheck />
                        </span>
                      </button>
                    )}

                    {isFolder && (
                      <button
                        className={styles.selectButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFolderToggle(item);
                        }}
                        disabled={loadingFolderId === item.id}
                        aria-pressed={isSelected}
                        aria-label={isSelected ? `Deselect ${item.name}` : `Select everything in ${item.name}`}
                      >
                        {loadingFolderId === item.id ? (
                          <CircularProgress size={19} style={{ color: '#5aa5ef' }} />
                        ) : (
                          <span className={`${styles.checkbox} ${isSelected ? styles.checkboxOn : ''}`}>
                            <IconCheck />
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                );
              })}

              {/* Loading more indicator */}
              {(isLoadingMore || isBackgroundLoading) && (
                <div className={styles.loadingMore}>
                  <CircularProgress size={24} style={{ color: '#5aa5ef' }} />
                  <span>
                    {isBackgroundLoading ? 'Loading all shared folders...' : 'Loading more...'}
                  </span>
                </div>
              )}

              {/* End of list indicator */}
              {!isBackgroundLoading && ((searchQuery.trim() && !hasMoreSearchResults && searchResults.length >= PAGE_SIZE)
                || (!searchQuery.trim() && !hasMore && !hasMoreSharedFolders && items.length >= PAGE_SIZE)
              ) && (
                <div className={styles.endOfList}>
                  No more items
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer — a selection summary and the two decisions, nothing else.
            Select all / Clear moved up into the path bar. */}
        <div className={styles.footer}>
          <div className={styles.footerInfo}>
            {selectedFiles.length > 0 ? (
              <span className={styles.selectedCount}>
                <span className={styles.selectedDot} aria-hidden="true" />
                {selectedFiles.length}
                {' '}
                file
                {selectedFiles.length !== 1 ? 's' : ''}
                {selectedBytes > 0 ? ` · ${formatFileSize(selectedBytes)}` : ''}
              </span>
            ) : (
              <span className={styles.footerHint}>Nothing selected yet</span>
            )}
          </div>
          <div className={styles.footerActions}>
            <button className={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
            <button
              className={styles.importButton}
              onClick={handleImport}
              disabled={selectedFiles.length === 0}
            >
              <IconDownload />
              {selectedFiles.length > 0 ? `Import ${selectedFiles.length}` : 'Import'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
